<script setup lang="ts">
    const props = defineProps<{
        controller: ReturnType<typeof useModal>;
        side: 'left' | 'right';
        type: string;
    }>();

    const { controller } = toRefs(props);
    const { isOpen, close } = controller.value;

    const sidebarContentElement = ref();
    onClickOutside(sidebarContentElement, () => close());

    const { clampBody, releaseBody } = useFixedBody();
    let isBodyClamped = false;

    const setBodyClamped = (clamped: boolean) => {
        if (clamped === isBodyClamped) return;
        isBodyClamped = clamped;
        if (clamped) {
            clampBody();
        } else {
            releaseBody();
        }
    };

    // immediate, because CheckoutSideCart re-keys this component whenever it opens or closes
    onMounted(() => watch(isOpen, setBodyClamped, { immediate: true }));
    onUnmounted(() => setBodyClamped(false));
</script>

<template>
    <Teleport to="body">
        <Transition :name="`sidebar-${side}`">
            <div
                v-if="isOpen"
                class="modal"
                :class="[type, side]"
                :data-testid="`sidebar-${side}`"
            >
                <div
                    ref="sidebarContentElement"
                    class="modal-wrapper"
                >
                    <slot></slot>

                    <svg
                        class="background"
                        viewBox="0 0 430 932"
                    >
                        <path
                            d="M-406.568 289.895-116.673 0l296.452 296.454c156.503 156.503 156.193 410.555-.692 567.44l-289.894 289.894-296.454-296.453c-156.503-156.503-156.19-410.555.693-567.44Z"
                            style="fill: url(#m-a-modal); fill-rule: nonzero; transform: translateY(40%)"
                        />
                        <path
                            d="m-314.86-76.453 857.574-8.863 8.12 785.792c4.285 414.836-368.468 755.013-832.572 759.809l-857.581 8.86-8.112-785.79c-4.29-414.834 368.467-755.011 832.571-759.808Z"
                            style="fill: url(#m-b-modal); fill-rule: nonzero"
                        />
                        <path
                            d="m623.269 321.139 496.664 133.08-121.941 455.089c-64.375 240.251-334.455 376.628-603.242 304.606l-496.657-133.079L20.034 625.746c64.375-240.25 334.455-376.627 603.235-304.607Z"
                            style="fill: url(#m-c-modal); fill-rule: nonzero; transform: translateY(40%)"
                        />

                        <defs>
                            <linearGradient
                                id="m-a-modal"
                                x1="0"
                                x2="1"
                                y1="0"
                                y2="0"
                                gradientTransform="rotate(-87.258 458.22 585.363) scale(656.2651)"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop
                                    offset="0"
                                    style="stop-color: #ff9800; stop-opacity: 0.7"
                                />
                                <stop
                                    offset="0"
                                    style="stop-color: #ff9800; stop-opacity: 0.7"
                                />
                                <stop
                                    offset=".95"
                                    style="stop-color: #2594a8; stop-opacity: 0.7"
                                />
                                <stop
                                    offset="1"
                                    style="stop-color: #2594a8; stop-opacity: 0.7"
                                />
                            </linearGradient>
                            <linearGradient
                                id="m-b-modal"
                                x1="0"
                                x2="1"
                                y1="0"
                                y2="0"
                                gradientTransform="rotate(-47.241 1503.585 1418.563) scale(2005.53)"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop
                                    offset="0"
                                    style="stop-color: #ff9800; stop-opacity: 0.65"
                                />
                                <stop
                                    offset=".05"
                                    style="stop-color: #ff9800; stop-opacity: 0.65"
                                />
                                <stop
                                    offset=".95"
                                    style="stop-color: #2594a8; stop-opacity: 0.65"
                                />
                                <stop
                                    offset="1"
                                    style="stop-color: #2594a8; stop-opacity: 0.65"
                                />
                            </linearGradient>
                            <linearGradient
                                id="m-c-modal"
                                x1="0"
                                x2="1"
                                y1="0"
                                y2="0"
                                gradientTransform="rotate(-21.064 2624.683 435.71) scale(763.397)"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop
                                    offset="0"
                                    style="stop-color: #ff9800; stop-opacity: 0.7"
                                />
                                <stop
                                    offset=".05"
                                    style="stop-color: #ff9800; stop-opacity: 0.7"
                                />
                                <stop
                                    offset=".95"
                                    style="stop-color: #2594a8; stop-opacity: 0.7"
                                />
                                <stop
                                    offset="1"
                                    style="stop-color: #2594a8; stop-opacity: 0.7"
                                />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
    @import '~/assets/scss/navigation/layout-sidebar.scss';
</style>
